import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type PropsWithChildren,
} from "react";

import { useGetFederationContext } from "src/api/federation";
import { useAuthContext } from "src/auth/hooks";
import {
  createStandardFederationContext,
  type FederationCapability,
  type FederationContextResponse,
} from "src/types/federation";


export type FederationContextValue = {
  context: FederationContextResponse;
  loading: boolean;
  error: unknown;
  hasCapability: (capability: FederationCapability) => boolean;
};

const defaultValue: FederationContextValue = {
  context: createStandardFederationContext(0),
  loading: false,
  error: null,
  hasCapability: () => false,
};

export const FederationContext =
  createContext<FederationContextValue>(defaultValue);

export function FederationContextProvider({ children }: PropsWithChildren) {
  const { currentProvider } = useAuthContext();
  const providerId = Number(currentProvider) || 0;
  const {
    federationContext,
    federationContextLoading,
    federationContextError,
  } = useGetFederationContext(providerId);

  const context = useMemo(() => {
    if (
      federationContextError ||
      !federationContext ||
      federationContext.workspace_provider_id !== providerId
    ) {
      return createStandardFederationContext(providerId);
    }
    return federationContext;
  }, [federationContext, federationContextError, providerId]);

  const hasCapability = useCallback(
    (capability: FederationCapability) =>
      !federationContextLoading &&
      !federationContextError &&
      context.workspace_provider_id === providerId &&
      context.capabilities.includes(capability),
    [context, federationContextError, federationContextLoading, providerId],
  );

  const value = useMemo<FederationContextValue>(
    () => ({
      context,
      loading: providerId > 0 && federationContextLoading,
      error: federationContextError,
      hasCapability,
    }),
    [context, federationContextError, federationContextLoading, hasCapability, providerId],
  );

  return (
    <FederationContext.Provider value={value}>
      {children}
    </FederationContext.Provider>
  );
}

export function useFederationContext() {
  return useContext(FederationContext);
}
